import { TOKEN_IDS } from "@/types";
import {
    useDialogPortal,
    useAnnualProjectedEarnings,
    useTransactionFees,
    useTransactionExecution,
    useLockGasEstimation,
    useLockPositions,
} from "@/hooks";
import { lockEventEmitter, LOCK_EVENTS } from "@/events";

import {
    estimateIncreaseUnwindingEpochsTransactionGas,
    prepareIncreaseUnwindingEpochsTransaction,
    gatewayIncreaseUnwindingEpochsTransaction,
} from "@/services";
import { useCallback } from "react";
import type { WalletClient } from "viem";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/config";
import { useAccount } from "wagmi";

interface ChangeLockDurationTransactionProps {
    existingDuration: number;
    newDuration: number;
    lockAmount: bigint;
    hasApproval: boolean;
}

export function useChangeDurationTransaction({
    lockAmount: _lockAmount,
    existingDuration,
    newDuration,
    hasApproval,
}: ChangeLockDurationTransactionProps) {
    const { invalidateQueries } = useQueryClient();
    const { address } = useAccount();
    const { closeDialog } = useDialogPortal();
    const { calculateProjectedEarnings, apy } = useAnnualProjectedEarnings(newDuration, Number(_lockAmount));

    const { lockedPositions } = useLockPositions();
    const position = lockedPositions.find((pos) => pos.lockDuration === `${existingDuration}w`);
    const shares: bigint = position ? BigInt(position.liUSDBalance) : BigInt(0);

    const estimateGasFn = useCallback(
        (walletClient: WalletClient) => {
            return estimateIncreaseUnwindingEpochsTransactionGas(existingDuration, newDuration, shares, walletClient);
        },
        [newDuration, existingDuration, shares],
    );

    const prepareTransactionFn = useCallback(
        async (walletClient: WalletClient) => {
            return prepareIncreaseUnwindingEpochsTransaction(existingDuration, newDuration, shares, walletClient);
        },
        [newDuration, existingDuration, shares],
    );

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useLockGasEstimation({
        estimateGasFn,
        prepareTransactionFn,
        hasApproval,
        lockDuration: existingDuration,
    });

    const { gasPriceUSD } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const executeTransactionFn = useCallback(
        async (walletClient: WalletClient) => {
            try {
                const tx = await gatewayIncreaseUnwindingEpochsTransaction(
                    existingDuration,
                    newDuration,
                    shares,
                    walletClient,
                );

                return tx;
            } finally {
                invalidateQueries({
                    queryKey: [[QUERY_KEYS.POSITIONS.LOCK.LIST(address)], [QUERY_KEYS.TOKENS.BALANCE.LIST(address)]],
                });
            }
        },
        [newDuration, existingDuration, shares, address, invalidateQueries],
    );

    const {
        isExecuting,
        error: transactionError,
        executeTransaction: executeChangeDurationTransaction,
    } = useTransactionExecution({
        executeTransactionFn,
        onSuccess: (hash) => {
            lockEventEmitter.emit(LOCK_EVENTS.SUCCESS, {
                transactionHash: hash,
                lockTokenId: TOKEN_IDS.IUSD,
                lockAmount: Number(position?.lockedBalance),
            });
            closeDialog();
            invalidateQueries({
                queryKey: [[QUERY_KEYS.POSITIONS.LOCK.LIST(address)], [QUERY_KEYS.TOKENS.BALANCE.LIST(address)]],
            });
        },
        errorMessage: "Failed to change withdrawal period",
    });

    const projectedEarnings = calculateProjectedEarnings("0"); // Since we're only changing duration, amount is 0

    return {
        gasPriceUSD,
        apy,
        projectedEarnings,
        executeChangeDurationTransaction,
        isLoading: isExecuting,
        isEstimatingGas,
        needsApproval: !hasApproval,
        error: gasError || transactionError,
        gasEstimate,
        isExecuting,
    };
}
